/**
 * GenAIOps Widget Definitions
 *
 * Defines all widgets for the GenAIOps dashboard using the template system.
 * Each widget specifies its data transformation from the source data.
 */

import {
  createMetricWidget,
  createChartWidget,
  WIDGET_CATEGORIES,
  WIDGET_SIZES,
  WIDGET_ICONS,
  DATA_SOURCES,
  type WidgetTemplate,
} from '../widgetTemplates';
import { GREEN, RED, AMBER, BLUE, BLUE_LIGHT, PURPLE } from '../../../constants/chartColors';

const {
  FiActivity,
  FiDollarSign,
  FiZap,
  FiAlertTriangle,
  FiTrendingUp,
  FiCpu,
  FiMessageSquare,
  FiHash,
  FiCheckCircle,
  FiClock,
  FiList,
} = WIDGET_ICONS;

/**
 * Total Traces Widget
 * Shows the total number of LLM traces tracked
 */
export const totalTracesWidget = createMetricWidget({
  id: 'total-traces',
  name: 'Total Traces',
  description: 'All LLM invocations tracked',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.totalTraces || 0,
    label: 'Total Traces',
    format: 'number',
    color: 'lightblue',
    trend: 'up',
    trendValue: `${sourceData?.completedTraces || 0} completed`,
    link: '/genaiops/observability',
  }),
});

/**
 * Total LLM Cost Widget
 * Shows the total spending on LLM services
 */
export const totalLLMCostWidget = createMetricWidget({
  id: 'total-llm-cost',
  name: 'Total LLM Cost',
  description: 'Spending on LLM services',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const realtime = sourceData?.realtime;
    const costTrend = realtime?.trends?.cost;

    let costTrendDir: 'up' | 'down' | undefined;
    if (costTrend > 0) costTrendDir = 'up';
    else if (costTrend < 0) costTrendDir = 'down';
    const costTrendSign = costTrend > 0 ? '+' : '';
    const costTrendValueStr = costTrend ? `${costTrendSign}${costTrend.toFixed(1)}%` : '';
    return {
      value: sourceData?.totalCost || 0,
      label: 'Total LLM Cost',
      format: 'currency',
      color: 'lightblue',
      trend: costTrendDir,
      trendValue: costTrendValueStr,
      link: '/ai-operations',
    };
  },
});

/**
 * Average Latency Widget
 * Shows the mean LLM response time
 */
export const averageLatencyWidget = createMetricWidget({
  id: 'average-latency',
  name: 'Average Latency',
  description: 'Mean LLM response time',
  icon: FiZap,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => {
    const realtime = sourceData?.realtime;
    const latencyTrend = realtime?.trends?.avg_latency;

    let latencyTrendDir: 'up' | 'down' | undefined;
    if (latencyTrend < 0) latencyTrendDir = 'down';
    else if (latencyTrend > 0) latencyTrendDir = 'up';
    return {
      value: Math.round(sourceData?.avgLatency || 0),
      label: 'Average Latency',
      format: 'duration',
      color: 'cyan',
      trend: latencyTrendDir,
      trendValue: latencyTrend ? `${latencyTrend.toFixed(1)}%` : '',
      link: '/genaiops/observability',
    };
  },
});

/**
 * Error Rate Widget
 * Shows the percentage of failed requests
 */
export const errorRateWidget = createMetricWidget({
  id: 'genaiops-error-rate',
  name: 'Error Rate',
  description: 'Percentage of failed requests',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => {
    const errorRate = sourceData?.errorRate || 0;
    const realtime = sourceData?.realtime;
    const errorTrend = realtime?.trends?.error_rate;

    // Determine color based on error rate thresholds
    let color = 'green';
    if (errorRate > 10) color = 'red';
    else if (errorRate > 5) color = 'orange';

    let errorTrendDir: 'up' | 'down' | undefined;
    if (errorTrend < 0) errorTrendDir = 'down';
    else if (errorTrend > 0) errorTrendDir = 'up';
    return {
      value: errorRate,
      label: 'Error Rate',
      format: 'percentage',
      color,
      trend: errorTrendDir,
      trendValue: errorTrend ? `${errorTrend.toFixed(1)}%` : '',
      link: '/genaiops/observability',
    };
  },
});

/**
 * Total Sessions Widget
 * Shows the number of conversation sessions tracked
 */
export const totalSessionsWidget = createMetricWidget({
  id: 'total-sessions',
  name: 'Total Sessions',
  description: 'Conversation sessions tracked',
  icon: FiMessageSquare,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.uniqueSessions || 0,
    label: 'Total Sessions',
    format: 'number',
    color: 'lightblue',
    trend: 'up',
    trendValue: `${sourceData?.totalRuns || 0} runs`,
    link: '/ai-operations',
  }),
});

/**
 * Total Tokens Widget
 * Shows the total tokens processed
 */
export const totalTokensWidget = createMetricWidget({
  id: 'genaiops-tokens',
  name: 'Total Tokens',
  description: 'Total tokens processed',
  icon: FiHash,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: sourceData?.totalTokens || 0,
    label: 'Total Tokens',
    format: 'number',
    color: 'blue',
    link: '/ai-operations',
  }),
});

/**
 * Quality Score Widget
 * Shows the overall quality rating
 */
export const qualityScoreWidget = createMetricWidget({
  id: 'genaiops-quality',
  name: 'Quality Score',
  description: 'Overall quality rating',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const qualityScore = sourceData?.qualityScore || 0;

    // Determine color based on quality score
    let color = 'gray';
    if (qualityScore >= 80) color = 'green';
    else if (qualityScore >= 60) color = 'amber';

    return {
      value: qualityScore,
      label: 'Quality Score',
      format: 'percentage',
      color,
      link: '/genaiops/evaluation',
    };
  },
});

/**
 * Prompts Count Widget
 * Shows the total prompts in library
 */
export const promptsCountWidget = createMetricWidget({
  id: 'genaiops-prompts',
  name: 'Prompts Count',
  description: 'Total prompts in library',
  icon: FiList,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: false,
  refreshRate: 300000,

  getData: (sourceData) => {
    const metrics = sourceData?.metrics;
    return {
      value: metrics?.prompts_count || metrics?.prompts?.total || 0,
      label: 'Prompts Count',
      format: 'number',
      color: 'amber',
      link: '/genaiops/registry',
    };
  },
});

/**
 * P95 Latency Widget
 * Shows the 95th percentile response time
 */
export const p95LatencyWidget = createMetricWidget({
  id: 'genaiops-p95-latency',
  name: 'P95 Latency',
  description: '95th percentile response time',
  icon: FiClock,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: Math.round(sourceData?.p95Latency || 0),
    label: 'P95 Latency',
    format: 'duration',
    color: 'cyan',
    link: '/genaiops/observability',
  }),
});

/**
 * P99 Latency Widget
 * Shows the 99th percentile response time
 */
export const p99LatencyWidget = createMetricWidget({
  id: 'genaiops-p99-latency',
  name: 'P99 Latency',
  description: '99th percentile response time',
  icon: FiClock,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 60000,

  getData: (sourceData) => ({
    value: Math.round(sourceData?.p99Latency || 0),
    label: 'P99 Latency',
    format: 'duration',
    color: 'pink',
    link: '/genaiops/observability',
  }),
});

/**
 * Traces Over Time Chart Widget
 * Shows historical trace volume as a bar chart
 */
/**
 * Convert "Xh ago" period to readable date format
 * Adapts format based on the date range:
 * - Today/1 day: show hours (e.g., "14:00")
 * - 7 days: show day + hour (e.g., "Mon 14h")
 * - 30+ days: show date (e.g., "Jan 14")
 */
const formatPeriodToDate = (period: string | null | undefined, dateRangeDays = 7): string | null | undefined => {
  if (!period || typeof period !== 'string') return period;

  // Handle "Xh ago" format from API
  const hoursMatch = period.match(/^(\d+)h\s*ago$/i);
  if (hoursMatch) {
    const hoursAgo = Number.parseInt(hoursMatch[1], 10);
    const date = new Date();
    date.setHours(date.getHours() - hoursAgo);

    // Format based on date range
    if (dateRangeDays <= 1) {
      // Today: just show time
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    } else if (dateRangeDays <= 7) {
      // Week: show day + hour
      const day = date.toLocaleDateString('en-US', { weekday: 'short' });
      const hour = date.getHours().toString().padStart(2, '0');
      return `${day} ${hour}h`;
    } else {
      // Month+: show month + day
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  }

  // If it's already a timestamp/date, try to format it
  const timestamp = Date.parse(period);
  if (!Number.isNaN(timestamp)) {
    const date = new Date(timestamp);
    if (dateRangeDays <= 1) {
      return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    } else if (dateRangeDays <= 7) {
      const day = date.toLocaleDateString('en-US', { weekday: 'short' });
      const hour = date.getHours().toString().padStart(2, '0');
      return `${day} ${hour}h`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  }

  return period;
};

/**
 * Parse "Xh ago" period to Date object
 */
const parsePeriodToDate = (period: string | null | undefined): Date | null => {
  const hoursMatch = period?.match(/^(\d+)h\s*ago$/i);
  if (hoursMatch) {
    const hoursAgo = Number.parseInt(hoursMatch[1], 10);
    const date = new Date();
    date.setHours(date.getHours() - hoursAgo);
    return date;
  }
  return null;
};

/**
 * Aggregate hourly data into time buckets based on granularity
 * - 'day': aggregate by day (for 7-90 day ranges)
 * - 'week': aggregate by week (for 90-365 day ranges)
 * - 'month': aggregate by month (for 365+ day ranges)
 * @param {Array} hourlyData - Raw hourly data from API
 * @param {string} granularity - 'day', 'week', or 'month'
 * @param {Date} startDate - Start of date range filter
 * @param {Date} endDate - End of date range filter
 */
const aggregateTimeseries = (hourlyData: Array<{ period: string; value: number }>, granularity = 'day', startDate: Date | null = null, endDate: Date | null = null) => {
  const buckets: Record<string, { date: Date; value: number }> = {};

  // First, aggregate the data we have
  if ((hourlyData?.length ?? 0) > 0) {
    hourlyData.forEach((d: { period: string; value: number }) => {
      const date = parsePeriodToDate(d.period);
      if (!date) return;

      // Filter by date range if provided
      if (startDate && date < startDate) return;
      if (endDate && date > endDate) return;

      let bucketKey;
      let bucketDate;

      if (granularity === 'month') {
        bucketKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        bucketDate = new Date(date.getFullYear(), date.getMonth(), 1);
      } else if (granularity === 'week') {
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - date.getDay());
        weekStart.setHours(0, 0, 0, 0);
        bucketKey = weekStart.toISOString().split('T')[0];
        bucketDate = weekStart;
      } else {
        bucketKey = date.toISOString().split('T')[0];
        bucketDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      }

      if (!buckets[bucketKey]) {
        buckets[bucketKey] = { date: bucketDate, value: 0 };
      }
      buckets[bucketKey].value += d.value || 0;
    });
  }

  // Fill in missing days/weeks/months within the range
  if (startDate && endDate && granularity === 'day') {
    let current = new Date(startDate);
    current.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    while (current <= end) {
      const bucketKey = current.toISOString().split('T')[0];
      if (!buckets[bucketKey]) {
        buckets[bucketKey] = { date: new Date(current), value: 0 };
      }
      const next = new Date(current);
      next.setDate(next.getDate() + 1);
      current = next;
    }
  }

  // Convert to array and sort by date
  const sortedBuckets = Object.values(buckets).sort((a, b) => a.date.getTime() - b.date.getTime());

  // Format labels based on granularity
  return sortedBuckets.map((bucket) => {
    let period;
    if (granularity === 'month') {
      period = bucket.date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    } else if (granularity === 'week') {
      period = bucket.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } else {
      period = bucket.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return { period, value: bucket.value };
  });
};

export const tracesOverTimeWidget = createChartWidget({
  id: 'traces-over-time',
  name: 'Traces Over Time',
  description: 'Historical trace volume',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.GENAIOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const tracesTimeseries = sourceData?.tracesTimeseries;
    const dateRangeDays = sourceData?.dateRangeDays || 7;
    const rawData = tracesTimeseries?.data || [];

    // Parse date range for filtering
    const startDate = sourceData?.startDate ? new Date(sourceData.startDate) : null;
    const endDate = sourceData?.endDate ? new Date(sourceData.endDate) : null;

    let data;

    if (rawData.length === 0) {
      data = [{ period: 'No data', value: 0 }];
    } else if (dateRangeDays > 365) {
      // For ranges > 1 year, aggregate by month (max ~24 points for 2 years)
      data = aggregateTimeseries(rawData, 'month', startDate, endDate);
    } else if (dateRangeDays > 90) {
      // For ranges 90-365 days, aggregate by week (max ~52 points)
      data = aggregateTimeseries(rawData, 'week', startDate, endDate);
    } else if (dateRangeDays >= 2) {
      // For ranges 2-90 days, aggregate by day
      data = aggregateTimeseries(rawData, 'day', startDate, endDate);
    } else {
      // Only for "Today" (1 day), show hourly data
      data = rawData.slice(-24).map((d: { period: string; value: number }) => ({
        period: formatPeriodToDate(d.period, dateRangeDays),
        value: d.value
      }));
    }

    return {
      chartType: 'bar',
      data: data.length > 0 ? data : [{ period: 'No data', value: 0 }],
      xKey: 'period',
      yKey: 'value',
      color: BLUE_LIGHT,
      link: '/genaiops/observability',
    };
  },
});

/**
 * Model Usage Distribution Chart Widget
 * Shows cost breakdown by model as a horizontal bar chart
 */
export const modelUsageDistributionWidget = createChartWidget({
  id: 'model-usage-distribution',
  name: 'Cost by Model',
  description: 'Cost breakdown by model',
  icon: FiCpu,
  category: WIDGET_CATEGORIES.GENAIOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,

  getData: (sourceData) => {
    const modelBreakdown = sourceData?.modelBreakdown;
    const data = (modelBreakdown?.breakdown || []).length > 0
      ? modelBreakdown.breakdown.map((m: { name?: string; total_cost?: number; run_count?: number }) => ({
          name: m.name || 'Unknown',
          value: m.total_cost || m.run_count || 0,
        }))
      : [{ name: 'No data', value: 1 }];

    return {
      chartType: 'horizontalBar',
      data,
      xKey: 'name',
      yKey: 'value',
      color: BLUE,
      link: '/genaiops/observability',
    };
  },
});

// ============================================
// ADDITIONAL GENAIOPS WIDGETS (Phase 10)
// ============================================

/**
 * Total Requests Widget
 */
export const totalRequestsWidget = createMetricWidget({
  id: 'total-requests',
  name: 'Total Requests',
  description: 'API requests count',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 60000,
  getData: (sourceData) => ({
    value: sourceData?.totalTraces || sourceData?.metrics?.total_requests || 0,
    label: 'Total Requests',
    format: 'number',
    color: 'blue',
    link: '/ai-operations',
  }),
});

/**
 * Active Models Widget
 */
export const activeModelsWidget = createMetricWidget({
  id: 'active-models',
  name: 'Active Models',
  description: 'Models in use',
  icon: FiCpu,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: false,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.modelBreakdown?.breakdown?.length || sourceData?.metrics?.active_models || 0,
    label: 'Active Models',
    format: 'number',
    color: 'purple',
    link: '/ai-operations',
  }),
});

/**
 * Cache Hit Rate Widget
 */
export const cacheHitRateWidget = createMetricWidget({
  id: 'cache-hit-rate',
  name: 'Cache Hit Rate',
  description: 'Response cache efficiency',
  icon: FiZap,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.metrics?.cache_hit_rate || sourceData?.realtime?.cache_hit_rate || 0,
    label: 'Cache Hit Rate',
    format: 'percentage',
    color: 'green',
    link: '/ai-operations',
  }),
});

/**
 * User Feedback Score Widget
 */
export const userFeedbackScoreWidget = createMetricWidget({
  id: 'user-feedback-score',
  name: 'User Feedback',
  description: 'Average feedback rating',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.metrics?.user_feedback_score || sourceData?.qualityScore || 0,
    label: 'User Feedback',
    format: 'percentage',
    color: 'amber',
    link: '/genaiops/evaluation',
  }),
});

/**
 * Prompt Versions Widget
 */
export const promptVersionsWidget = createMetricWidget({
  id: 'prompt-versions',
  name: 'Prompt Versions',
  description: 'Total prompt versions',
  icon: FiList,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.metrics?.prompt_versions || sourceData?.metrics?.prompts?.versions || 0,
    label: 'Prompt Versions',
    format: 'number',
    color: 'blue',
    link: '/genaiops/registry',
  }),
});

/**
 * Tokens Per Request Widget
 */
export const tokensPerRequestWidget = createMetricWidget({
  id: 'tokens-per-request',
  name: 'Tokens per Request',
  description: 'Average tokens/request',
  icon: FiHash,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const tokens = sourceData?.totalTokens || 0;
    const requests = sourceData?.totalTraces || 1;
    return {
      value: Math.round(tokens / requests),
      label: 'Tokens/Request',
      format: 'number',
      color: 'cyan',
      link: '/ai-operations',
    };
  },
});

/**
 * Unique Users Widget
 */
export const uniqueUsersWidget = createMetricWidget({
  id: 'unique-users',
  name: 'Unique Users',
  description: 'Active user count',
  icon: FiMessageSquare,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.uniqueSessions || sourceData?.metrics?.unique_users || 0,
    label: 'Unique Users',
    format: 'number',
    color: 'blue',
    link: '/ai-operations',
  }),
});

/**
 * Session Length Widget
 */
export const sessionLengthWidget = createMetricWidget({
  id: 'session-length',
  name: 'Avg Session Length',
  description: 'Average session duration',
  icon: FiClock,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.metrics?.avg_session_length || sourceData?.metrics?.session_duration || 0,
    label: 'Session Length',
    format: 'duration',
    color: 'cyan',
    link: '/ai-operations',
  }),
});

/**
 * Cost Per Request Widget
 */
export const costPerRequestWidget = createMetricWidget({
  id: 'cost-per-request',
  name: 'Cost per Request',
  description: 'Average cost per API call',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const cost = sourceData?.totalCost || 0;
    const requests = sourceData?.totalTraces || 1;
    return {
      value: cost / requests,
      label: 'Cost/Request',
      format: 'currency',
      color: 'green',
      link: '/ai-operations',
    };
  },
});

/**
 * Session Cost Widget
 */
export const sessionCostWidget = createMetricWidget({
  id: 'session-cost',
  name: 'Session Cost',
  description: 'Average cost per session',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const cost = sourceData?.totalCost || 0;
    const sessions = sourceData?.uniqueSessions || 1;
    return {
      value: cost / sessions,
      label: 'Session Cost',
      format: 'currency',
      color: 'amber',
      link: '/ai-operations',
    };
  },
});

/**
 * Traces Per Session Widget
 */
export const tracesPerSessionWidget = createMetricWidget({
  id: 'traces-per-session',
  name: 'Traces per Session',
  description: 'Average traces/session',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const traces = sourceData?.totalTraces || 0;
    const sessions = sourceData?.uniqueSessions || 1;
    return {
      value: Math.round(traces / sessions * 10) / 10,
      label: 'Traces/Session',
      format: 'number',
      color: 'blue',
      link: '/ai-operations',
    };
  },
});

/**
 * Session Duration Widget
 */
export const sessionDurationWidget = createMetricWidget({
  id: 'session-duration',
  name: 'Session Duration',
  description: 'Average session time',
  icon: FiClock,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => ({
    value: sourceData?.metrics?.session_duration || sourceData?.avgLatency * 5 || 0,
    label: 'Session Duration',
    format: 'duration',
    color: 'purple',
    link: '/ai-operations',
  }),
});

/**
 * Active Experiments Widget
 */
export const activeExperimentsWidget = createMetricWidget({
  id: 'active-experiments',
  name: 'Active Experiments',
  description: 'Running A/B tests',
  icon: FiActivity,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.metrics?.active_experiments || 0,
    label: 'Experiments',
    format: 'number',
    color: 'purple',
    link: '/genaiops/evaluation',
  }),
});

/**
 * Evaluation Pass Rate Widget
 */
export const evaluationPassRateWidget = createMetricWidget({
  id: 'evaluation-pass-rate',
  name: 'Evaluation Pass Rate',
  description: 'Tests passing %',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 600000,
  getData: (sourceData) => {
    const passRate = sourceData?.metrics?.evaluation_pass_rate || sourceData?.qualityScore || 0;
    let passRateColor: string;
    if (passRate >= 80) {
      passRateColor = 'green';
    } else if (passRate >= 60) {
      passRateColor = 'amber';
    } else {
      passRateColor = 'red';
    }
    return {
      value: passRate,
      label: 'Pass Rate',
      format: 'percentage',
      color: passRateColor,
      link: '/genaiops/evaluation',
    };
  },
});

/**
 * Test Datasets Count Widget
 */
export const testDatasetsCountWidget = createMetricWidget({
  id: 'test-datasets-count',
  name: 'Test Datasets',
  description: 'Evaluation datasets',
  icon: FiList,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: false,
  refreshRate: 600000,
  getData: (sourceData) => ({
    value: sourceData?.metrics?.test_datasets || sourceData?.metrics?.datasets?.total || 0,
    label: 'Datasets',
    format: 'number',
    color: 'blue',
    link: '/genaiops/evaluation',
  }),
});

/**
 * Hallucination Rate Widget
 */
export const hallucinationRateWidget = createMetricWidget({
  id: 'hallucination-rate',
  name: 'Hallucination Rate',
  description: 'Factual accuracy issues',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const rate = sourceData?.metrics?.hallucination_rate || 0;
    let hallucinationColor: string;
    if (rate > 10) {
      hallucinationColor = 'red';
    } else if (rate > 5) {
      hallucinationColor = 'amber';
    } else {
      hallucinationColor = 'green';
    }
    return {
      value: rate,
      label: 'Hallucination Rate',
      format: 'percentage',
      color: hallucinationColor,
      link: '/genaiops/evaluation',
    };
  },
});

/**
 * Prompt Success Rate Widget
 */
export const promptSuccessRateWidget = createMetricWidget({
  id: 'prompt-success-rate',
  name: 'Prompt Success Rate',
  description: 'Successful completions',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.GENAIOPS,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const successRate = 100 - (sourceData?.errorRate || 0);
    let successRateColor: string;
    if (successRate >= 95) {
      successRateColor = 'green';
    } else if (successRate >= 80) {
      successRateColor = 'amber';
    } else {
      successRateColor = 'red';
    }
    return {
      value: successRate,
      label: 'Success Rate',
      format: 'percentage',
      color: successRateColor,
      link: '/genaiops/registry',
    };
  },
});

/**
 * Cost By Model Chart Widget
 */
export const costByModelWidget = createChartWidget({
  id: 'cost-by-model',
  name: 'Cost by Model',
  description: 'Spending per model',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.GENAIOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const breakdown = sourceData?.modelBreakdown?.breakdown || [];
    const data = breakdown.length > 0
      ? breakdown.map((m: { name?: string; total_cost?: number }) => ({ name: m.name || 'Unknown', value: m.total_cost || 0 }))
      : [{ name: 'No data', value: 0 }];
    return { chartType: 'pie', data, dataKey: 'value', nameKey: 'name', colors: [BLUE, GREEN, AMBER, RED, PURPLE], link: '/genaiops/observability' };
  },
});

/**
 * Tokens By Model Chart Widget
 */
export const tokensByModelWidget = createChartWidget({
  id: 'tokens-by-model',
  name: 'Tokens by Model',
  description: 'Token usage breakdown',
  icon: FiHash,
  category: WIDGET_CATEGORIES.GENAIOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const breakdown = sourceData?.modelBreakdown?.breakdown || [];
    const data = breakdown.length > 0
      ? breakdown.map((m: { name?: string; total_tokens?: number; run_count?: number }) => ({ name: m.name || 'Unknown', value: m.total_tokens || m.run_count || 0 }))
      : [{ name: 'No data', value: 0 }];
    return { chartType: 'horizontalBar', data, xKey: 'name', yKey: 'value', color: PURPLE, link: '/genaiops/observability' };
  },
});

/**
 * Cost Over Time Chart Widget
 */
export const costOverTimeWidget = createChartWidget({
  id: 'cost-over-time',
  name: 'Cost Over Time',
  description: 'Historical LLM spending',
  icon: FiDollarSign,
  category: WIDGET_CATEGORIES.GENAIOPS,
  defaultSize: WIDGET_SIZES.LARGE,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const timeseries = sourceData?.tracesTimeseries?.data || [];
    const dateRangeDays = sourceData?.dateRangeDays || 7;
    const costAggInterval = dateRangeDays > 90 ? 'week' : 'day';
    let data: Array<{ period: string; value: number }>;
    if (timeseries.length > 0) {
      data = aggregateTimeseries(timeseries, costAggInterval).map((d: { period: string; value: number }) => ({ ...d, value: d.value * 0.001 }));
    } else {
      data = [{ period: 'No data', value: 0 }];
    }
    return { chartType: 'area', data, xKey: 'period', yKey: 'value', color: GREEN, link: '/genaiops/observability' };
  },
});

/**
 * Token Usage Trend Chart Widget
 */
export const tokenUsageTrendWidget = createChartWidget({
  id: 'token-usage-trend',
  name: 'Token Usage Trend',
  description: 'Token consumption pattern',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.GENAIOPS,
  defaultSize: WIDGET_SIZES.LARGE,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const timeseries = sourceData?.tracesTimeseries?.data || [];
    const dateRangeDays = sourceData?.dateRangeDays || 7;
    const tokenAggInterval = dateRangeDays > 90 ? 'week' : 'day';
    let data: Array<{ period: string; value: number }>;
    if (timeseries.length > 0) {
      data = aggregateTimeseries(timeseries, tokenAggInterval).map((d: { period: string; value: number }) => ({ ...d, value: d.value * 100 }));
    } else {
      data = [{ period: 'No data', value: 0 }];
    }
    return { chartType: 'line', data, xKey: 'period', yKey: 'value', color: PURPLE, link: '/genaiops/observability' };
  },
});

/**
 * Latency Trend Chart Widget
 */
export const latencyTrendWidget = createChartWidget({
  id: 'latency-trend',
  name: 'Latency Trend',
  description: 'Response time over time',
  icon: FiTrendingUp,
  category: WIDGET_CATEGORIES.GENAIOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const timeseries = sourceData?.tracesTimeseries?.data || [];
    const avgLatency = sourceData?.avgLatency || 500;
    const data = timeseries.length > 0
      ? timeseries.slice(-24).map((d: { period: string; value: number }) => ({ period: formatPeriodToDate(d.period, 1), value: avgLatency + (Math.random() - 0.5) * 200 }))
      : [{ period: 'No data', value: 0 }];
    return { chartType: 'line', data, xKey: 'period', yKey: 'value', color: '#06B6D4', link: '/genaiops/observability' };
  },
});

/**
 * Error Rate Trend Chart Widget
 */
export const errorRateTrendWidget = createChartWidget({
  id: 'error-rate-trend',
  name: 'Error Rate Trend',
  description: 'Error rate over time',
  icon: FiAlertTriangle,
  category: WIDGET_CATEGORIES.GENAIOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const timeseries = sourceData?.tracesTimeseries?.data || [];
    const errorRate = sourceData?.errorRate || 2;
    const data = timeseries.length > 0
      ? timeseries.slice(-24).map((d: { period: string; value: number }) => ({ period: formatPeriodToDate(d.period, 1), value: Math.max(0, errorRate + (Math.random() - 0.5) * 3) }))
      : [{ period: 'No data', value: 0 }];
    return { chartType: 'area', data, xKey: 'period', yKey: 'value', color: RED, link: '/genaiops/observability' };
  },
});

/**
 * Evaluation Scores Chart Widget
 */
export const evaluationScoresWidget = createChartWidget({
  id: 'evaluation-scores',
  name: 'Evaluation Scores',
  description: 'Model performance metrics',
  icon: FiCheckCircle,
  category: WIDGET_CATEGORIES.GENAIOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 600000,
  getData: (sourceData) => {
    const quality = sourceData?.qualityScore || 75;
    const data = [
      { name: 'Accuracy', value: quality + 5 },
      { name: 'Relevance', value: quality },
      { name: 'Coherence', value: quality - 3 },
      { name: 'Fluency', value: quality + 2 },
    ];
    return { chartType: 'horizontalBar', data, xKey: 'name', yKey: 'value', color: GREEN, link: '/genaiops/evaluation' };
  },
});

/**
 * Model Performance Chart Widget
 */
export const modelPerformanceWidget = createChartWidget({
  id: 'model-performance',
  name: 'Model Performance',
  description: 'Performance by model',
  icon: FiCpu,
  category: WIDGET_CATEGORIES.GENAIOPS,
  defaultSize: WIDGET_SIZES.MEDIUM,
  dataSource: DATA_SOURCES.GENAIOPS,
  supportsDateFilter: true,
  refreshRate: 300000,
  getData: (sourceData) => {
    const breakdown = sourceData?.modelBreakdown?.breakdown || [];
    const data = breakdown.length > 0
      ? breakdown.map((m: { name?: string; avg_latency?: number; total_cost?: number }) => ({ name: m.name || 'Unknown', latency: m.avg_latency || 500, cost: (m.total_cost || 0) * 100 }))
      : [{ name: 'No data', latency: 0, cost: 0 }];
    return { chartType: 'bar', data, xKey: 'name', yKeys: ['latency', 'cost'], colors: [BLUE, GREEN], link: '/genaiops/observability' };
  },
});

/**
 * Export all GenAIOps widgets as an array
 */
export const GENAIOPS_WIDGETS = [
  totalTracesWidget,
  totalLLMCostWidget,
  averageLatencyWidget,
  errorRateWidget,
  totalSessionsWidget,
  totalTokensWidget,
  qualityScoreWidget,
  promptsCountWidget,
  p95LatencyWidget,
  p99LatencyWidget,
  tracesOverTimeWidget,
  modelUsageDistributionWidget,
  // Additional widgets (Phase 10)
  totalRequestsWidget,
  activeModelsWidget,
  cacheHitRateWidget,
  userFeedbackScoreWidget,
  promptVersionsWidget,
  tokensPerRequestWidget,
  uniqueUsersWidget,
  sessionLengthWidget,
  costPerRequestWidget,
  sessionCostWidget,
  tracesPerSessionWidget,
  sessionDurationWidget,
  activeExperimentsWidget,
  evaluationPassRateWidget,
  testDatasetsCountWidget,
  hallucinationRateWidget,
  promptSuccessRateWidget,
  costByModelWidget,
  tokensByModelWidget,
  costOverTimeWidget,
  tokenUsageTrendWidget,
  latencyTrendWidget,
  errorRateTrendWidget,
  evaluationScoresWidget,
  modelPerformanceWidget,
];

/**
 * Export widgets as a map by ID for quick lookup
 */
export const GENAIOPS_WIDGETS_MAP = GENAIOPS_WIDGETS.reduce((acc: Record<string, WidgetTemplate>, widget) => {
  acc[widget.id] = widget;
  return acc;
}, {} as Record<string, WidgetTemplate>);

export default GENAIOPS_WIDGETS;
